// Google Analytics 4 Utility Functions
import axios from 'axios';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * Get device ID from localStorage
 */
const getDeviceId = () => {
  let deviceId = localStorage.getItem('deviceId');
  if (!deviceId) {
    deviceId = 'device_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    localStorage.setItem('deviceId', deviceId);
  }
  return deviceId;
};

/**
 * Log PWA event to backend
 */
const logPWAEventToBackend = async (eventType, eventParams = {}) => {
  try {
    const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
    const deviceId = getDeviceId();
    
    await axios.post(`${API}/analytics/pwa-event`, {
      event_type: eventType,
      user_id: user?.id || null,
      platform: /iPhone|iPad|iPod/.test(navigator.userAgent) ? 'iOS' : 'Android',
      user_logged_in: !!user,
      user_agent: navigator.userAgent,
      device_id: deviceId,
      additional_data: eventParams
    });
  } catch (error) {
    console.error('Failed to log PWA event to backend:', error);
  }
};

/**
 * Track page view
 * @param {string} pagePath - Path of the page
 * @param {string} pageTitle - Title of the page
 */
export const trackPageView = (pagePath, pageTitle) => {
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle,
      page_location: window.location.href,
      send_to: 'G-QHZ0WNF43T'
    });
  }
};

/**
 * Track custom event
 * @param {string} eventName - Name of the event
 * @param {object} eventParams - Event parameters
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (window.gtag) {
    window.gtag('event', eventName, {
      ...eventParams,
      timestamp: new Date().toISOString(),
      domain: window.location.hostname
    });
  }
};

/**
 * Track PWA Installation
 */
export const trackPWAInstall = () => {
  const params = {
    event_category: 'PWA',
    event_label: 'App installed to home screen',
    platform: /iPhone|iPad|iPod/.test(navigator.userAgent) ? 'iOS' : 'Android'
  };
  
  trackEvent('pwa_installed', params);
  logPWAEventToBackend('pwa_installed', params);
};

/**
 * Track PWA Launch (from home screen)
 */
export const trackPWALaunch = () => {
  if (window.matchMedia('(display-mode: standalone)').matches) {
    const params = {
      event_category: 'PWA',
      event_label: 'App launched from home screen',
      platform: /iPhone|iPad|iPod/.test(navigator.userAgent) ? 'iOS' : 'Android'
    };
    
    trackEvent('pwa_launch', params);
    logPWAEventToBackend('pwa_launch', params);
  }
};

/**
 * Track user login
 * @param {string} method - Login method (google, email, etc)
 */
export const trackLogin = (method = 'google') => {
  trackEvent('login', {
    method: method,
    event_category: 'Authentication'
  });
};

/**
 * Track user signup
 * @param {string} method - Signup method
 */
export const trackSignup = (method = 'google') => {
  trackEvent('sign_up', {
    method: method,
    event_category: 'Authentication'
  });
};

/**
 * Track event creation
 * @param {string} planType - Plan type selected
 * @param {number} budget - Event budget
 */
export const trackEventCreation = (planType, budget) => {
  trackEvent('create_event', {
    event_category: 'Event Management',
    plan_type: planType,
    budget: budget
  });
};

/**
 * Track RSVP submission
 * @param {string} eventId - Event ID
 * @param {number} contributionAmount - Contribution amount
 */
export const trackRSVP = (eventId, contributionAmount) => {
  trackEvent('submit_rsvp', {
    event_category: 'Engagement',
    event_id: eventId,
    contribution_amount: contributionAmount
  });
};

/**
 * Track payment/contribution
 * @param {number} amount - Payment amount
 * @param {string} currency - Currency code
 */
export const trackPayment = (amount, currency = 'INR') => {
  trackEvent('purchase', {
    event_category: 'Ecommerce',
    value: amount,
    currency: currency
  });
};

/**
 * Track co-host invitation
 * @param {string} eventId - Event ID
 */
export const trackCoHostInvite = (eventId) => {
  trackEvent('cohost_invite', {
    event_category: 'Collaboration',
    event_id: eventId
  });
};

/**
 * Track search
 * @param {string} searchTerm - Search query
 */
export const trackSearch = (searchTerm) => {
  trackEvent('search', {
    search_term: searchTerm,
    event_category: 'Search'
  });
};

/**
 * Track button click
 * @param {string} buttonName - Name/label of button
 * @param {string} location - Where the button is located
 */
export const trackButtonClick = (buttonName, location) => {
  trackEvent('button_click', {
    event_category: 'Engagement',
    button_name: buttonName,
    button_location: location
  });
};

/**
 * Track error
 * @param {string} errorMessage - Error message
 * @param {string} errorLocation - Where error occurred
 */
export const trackError = (errorMessage, errorLocation) => {
  trackEvent('error', {
    event_category: 'Error',
    error_message: errorMessage,
    error_location: errorLocation
  });
};

/**
 * Set user properties
 * @param {string} userId - User ID
 * @param {object} userProperties - User properties
 */
export const setUserProperties = (userId, userProperties = {}) => {
  if (window.gtag) {
    window.gtag('set', 'user_properties', {
      user_id: userId,
      ...userProperties
    });
  }
};

/**
 * Track time on page
 * @param {string} pageName - Name of the page
 * @param {number} timeInSeconds - Time spent in seconds
 */
export const trackTimeOnPage = (pageName, timeInSeconds) => {
  trackEvent('time_on_page', {
    event_category: 'Engagement',
    page_name: pageName,
    time_seconds: timeInSeconds
  });
};
